import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { InsertDeviceCommandLogMutation, isNotNullOrUndefined, System } from "ngx-core-services";
import gql from "graphql-tag";
import { Store } from "@ngrx/store";
import { exhaustMap, first, map, shareReplay, switchMap } from "rxjs/operators";
import { cleanString, CommunicationChannelType, TypeInternetDevice, V3SafetyConfig } from "ngx-modbus";
import { MatDialog } from "@angular/material/dialog";
import { ColumnConfig, ColumnTypeEnum, DialogComponent, DialogDataComponent } from "ngx-zcs-components";
import * as Sentry from "@sentry/angular";
import { PopoverController } from "@ionic/angular";

import { CreateBlockReduxSupport, IReduxBlock } from "../../store/helpers/decorator";
import { CreateCustomBlockSizeType, ICustomBlock } from "../decorators";
import { SystemsState } from "../../store";
import { UPGRADE_COMMON_LABELS } from "../shared/labels";
import { ChannelHandler, DeviceModuleInfo, UpgradeSGPService } from "../../communication";
import { selectedSystemInvertersWithLoggerData } from "../../store/selectors/system.selectors";
import { SafetyDetails } from "../../SGP";
import { createDebugLogger, DebugConfig } from '../shared/debug';

const SAFETY_DEBUG_CONFIG: DebugConfig = { ENABLED: false, LOG_API: false, LOG_STATE: false, LOG_DATA: false };
const Logger = createDebugLogger('[UpgradeSafety]', SAFETY_DEBUG_CONFIG);

const labels = {
  ...UPGRADE_COMMON_LABELS,
  safetyTitle: 'Safety file list',
  confirmTitle: 'Upgrade Confirmation',
  upgrade: 'Upgrade',
};

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-768': 768,
})
@Component({
  selector: 'lib-upgrade-safety',
  templateUrl: './upgrade-safety.component.html',
  styleUrls: ['./upgrade-safety.component.scss'],
})
export class UpgradeSafetyComponent implements ICustomBlock, IReduxBlock, OnInit, OnDestroy {
  // Template references for azzurro-table custom columns
  @ViewChild('versionTemplate', { static: true }) versionTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;

  // Column configuration for azzurro-table (initialized in ngOnInit)
  tableColumns: ColumnConfig[] = [];

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly _inverterId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set inverterSn(value: string | undefined) {
    this._inverterId$.next(value);
  }

  @Input() uiFramework: 'material' | 'ionic' = 'material';

  /**
   * When true, disables all safety upgrade interactions.
   * Used to prevent safety upgrades while firmware upgrade is in progress.
   */
  @Input() disabled = false;

  // AH channel support (same pattern as settings-inverter)
  @Input() channelType?: CommunicationChannelType;
  @Input() gatewaySN?: string;
  @Input() modbusAddress?: number;

  private get isAH(): boolean {
    return this.channelType === CommunicationChannelType.InternetAH_ZCS;
  }

  updateState = this.upgradeSGPService.state;

  // Explicit trigger: emits once at init, then only after chooseSafety completes.
  // Avoids the decorator's _refresh$ which emits multiple times per store change.
  private readonly _safetyTrigger$ = new BehaviorSubject<void>(undefined);

  readonly safeties$ = combineLatest([
    this._inverterId$.pipe(isNotNullOrUndefined()),
    this._safetyTrigger$,
  ]).pipe(
    // exhaustMap: ignore re-triggers while the async operation is running
    exhaustMap(async ([serial]) => {
      let countryCode: number;
      let region: number;
      let version: number;
      let modules = { ARM: '', DSPM: '', DSPS: '' };

      if (this.isAH && this.channelHandler.handler.channel.command) {
        // AH post-write: channel is open, read fresh values via Modbus (same as Solarman)
        Logger.api(` AH: channel already open, reading safety via Modbus`);
        await this.channelHandler.handler.channel.command.executeMixedRequests([{
          mode: 'read',
          table: V3SafetyConfig,
        }]);
        const code = this.channelHandler.handler.channel.command.data.COUNTRY as number;
        version = this.channelHandler.handler.channel.command.data.SAFETY_VERSION as number;
        // eslint-disable-next-line no-bitwise
        countryCode = code >> 8;
        // eslint-disable-next-line no-bitwise
        region = code & 0xff;
        Logger.api(` AH Modbus read: countryCode=${countryCode}, region=${region}, version=${version}`);

        // Firmware modules still from store
        const devices = await this.store.select(selectedSystemInvertersWithLoggerData)
          .pipe(first()).toPromise();
        const device = devices?.find(d => d.id === serial);
        const cleanFw = (v?: string | null) => v?.replace(/^0+(?=[A-Za-z])/, '').toUpperCase() ?? '';
        modules = {
          ARM: cleanFw(device?.arm),
          DSPM: cleanFw(device?.dspm),
          DSPS: cleanFw(device?.dsps),
        };
      } else if (this.isAH) {
        // AH initial load: no channel open yet, read from store
        Logger.api(` AH: no channel, reading safety from store`);
        const devices = await this.store.select(selectedSystemInvertersWithLoggerData)
          .pipe(first()).toPromise();
        const device = devices?.find(d => d.id === serial);
        countryCode = device?.ahCountry ?? 0;
        region = device?.ahRegion ?? 0;
        version = device?.ahSafetyVer ? Number(device.ahSafetyVer) : 0;
        const cleanFw = (v?: string | null) => v?.replace(/^0+(?=[A-Za-z])/, '').toUpperCase() ?? '';
        modules = {
          ARM: cleanFw(device?.arm),
          DSPM: cleanFw(device?.dspm),
          DSPS: cleanFw(device?.dsps),
        };
      } else {
        // Solarman: read COUNTRY + SAFETY_VERSION via Modbus, firmware modules from store
        await this.getCurrentSafetyDetails();
        const code = this.channelHandler.handler.channel.command?.data.COUNTRY as number;
        version = this.channelHandler.handler.channel.command?.data.SAFETY_VERSION as number;
        // eslint-disable-next-line no-bitwise
        countryCode = code >> 8;
        // eslint-disable-next-line no-bitwise
        region = code & 0xff;

        const devices = await this.store.select(selectedSystemInvertersWithLoggerData)
          .pipe(first()).toPromise();
        const device = devices?.find(d => d.id === serial);
        const cleanFw = (v?: string | null) => v?.replace(/^0+(?=[A-Za-z])/, '').toUpperCase() ?? '';
        modules = {
          ARM: cleanFw(device?.arm),
          DSPM: cleanFw(device?.dspm),
          DSPS: cleanFw(device?.dsps),
        };
        Logger.api(` Solarman: modules from store: ARM=${modules.ARM}, DSPM=${modules.DSPM}, DSPS=${modules.DSPS}`);
      }

      const deviceModuleInfo: DeviceModuleInfo = {
        serial,
        modules,
      };

      const state = this.updateState;
      state.upgradeConfig = await this.upgradeSGPService.isDeviceUpgradable(deviceModuleInfo);
      await this.upgradeSGPService.downloadSafety();

      this.upgradeSGPService.state$.next(state);

      // AH: always show safety list regardless of upgradable status — the S3 safety
      // file list is available even if firmware modules are already up to date.
      if (!this.isAH && !state.upgradeConfig.upgradable) {
        return [];
      }

      const safetyList = state.safetyList.map((safety) => ({
        ...safety,
        selected: Number(safety.countryCode) === countryCode && Number(safety.regionCode) === region && Number(safety.version) === version,
      }));

      return safetyList;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  selectedSafety$ = this.safeties$.pipe(
    map((safeties) => safeties.find((safety) => safety.selected)),
    map((safety) => safety ? {
      ...safety,
      country: cleanString(safety.country),
      region: cleanString(safety.region),
      version: cleanString(safety.version),
    } : undefined),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  safetiesList$ = this.safeties$.pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  upgrading$ = this.upgradeSGPService.state$.pipe(
    map(state => state.isUpdating && state.totalProgress),
    map((progress) => ({
      isUpgrading: typeof progress === 'number',
      message: `${typeof progress === 'number' ? progress.toFixed(1) : ''}% - ${this.labels.upgradingSafety}`,
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    private dialog: MatDialog,
    private readonly channelHandler: ChannelHandler,
    private readonly insertDeviceCommandLogMutation: InsertDeviceCommandLogMutation,
    private readonly popoverController: PopoverController,
    private readonly upgradeSGPService: UpgradeSGPService,
    readonly store: Store<SystemsState>,
  ) {
    this.chooseSafety = this.chooseSafety.bind(this);
    this.logConfigurationSafeties = this.logConfigurationSafeties.bind(this);
    this.openConfirmDialog = this.openConfirmDialog.bind(this);
  }

  ngOnInit(): void {
    // Initialize table columns configuration
    this.tableColumns = [
      {
        columnDef: 'country',
        header: this.labels.country,
        type: ColumnTypeEnum.Text,
        cell: (row: any) => cleanString(row.country) ?? '',
        sortable: true,
      },
      {
        columnDef: 'region',
        header: this.labels.region,
        type: ColumnTypeEnum.Text,
        cell: (row: any) => cleanString(row.region)?.toUpperCase() ?? '',
        sortable: true,
      },
      {
        columnDef: 'version',
        header: this.labels.version,
        type: ColumnTypeEnum.Template,
        template: this.versionTemplate,
        sortable: true,
      },
      {
        columnDef: 'actions',
        header: this.labels.actions,
        type: ColumnTypeEnum.Template,
        template: this.actionsTemplate,
      },
    ];
  }

  async logConfigurationSafeties(item: SafetyDetails) {
    const insertQuery = gql`
      fragment responsestatustype on ResponseStatusType {
        success
        statusCode
        message
      }
    `;

    const device_sn = this.channelHandler.handler.channel.inverterId;
    const timestamp = Math.floor(Date.now() / 1000);
    const param_name = `${cleanString(item.country)} - ${cleanString(item.region)} - ${item.version}`;
    const param_value = `${item.countryCode} - ${item.regionCode} - ${item.version}`;
    const gateway_sn = `${this.channelHandler.handler.channel.loggerId}`;
    const string_log = `Safety file country ${cleanString(item.country)} , region ${cleanString(item.region)} and version ${item.version} selected`;
    const category = 'Safety';

    Sentry.captureEvent({
      message: `Config Safety File - ${device_sn}`,
      level: "info",
      tags: {
        operation: "safety-configuration",
        device_sn,
        gateway_sn,
      },
      extra: {
        param_name,
        param_value,
        category,
        string_log,
        timestamp,
      },
    });

    this.insertDeviceCommandLogMutation.execute(insertQuery, {
      input: { device_sn, timestamp, param_name, param_value, gateway_sn, string_log, category }
    });
  }

  async getCurrentSafetyDetails() {
    if (this.channelHandler.handler.channel.command === undefined) {
      const channel = this.channelType ?? CommunicationChannelType.Internet;
      this.channelHandler.switch(channel);

      const internetInput: TypeInternetDevice = {
        serial: this._inverterId$.value || '',
      };

      // AH: add gatewaySN and modbusAddress (same pattern as settings-inverter)
      if (channel === CommunicationChannelType.InternetAH_ZCS) {
        internetInput.gatewaySN = this.gatewaySN;
        internetInput.modbusAddress = this.modbusAddress;
        internetInput.deviceType = 'inverter';
      }

      if (this.channelHandler.handler.type === channel) {
        await this.channelHandler.handler.channel.open(internetInput as any);
      }
    }

    await this.channelHandler.handler.channel.command?.executeMixedRequests([{
      mode: 'read',
      table: V3SafetyConfig,
    }]);
  }

  async openConfirmDialog(title: string, content: string): Promise<boolean> {
    if (this.uiFramework === 'material') {
      const dialogRef = this.dialog.open(DialogDataComponent, {
        width: '600px',
      });
      const actions = [
        {
          text: this.labels.cancel,
          action: () => dialogRef.close(false),
        },
        {
          text: this.labels.confirm,
          cssClass: 'custom-mat-button-primary',
          focus: true,
          action: () => dialogRef.close(true),
        },
      ];
      dialogRef.componentInstance.data = {
        title,
        content,
        buttons: actions,
      };
      return dialogRef.afterClosed().toPromise();
    } else {
      const popover = await this.popoverController.create({
        component: DialogComponent,
        componentProps: {
          name: title,
          description: content,
          primary: { name: this.labels.confirm, click: () => popover.dismiss(true) },
          secondary: { name: this.labels.cancel, click: () => popover.dismiss(false) },
        },
        cssClass: 'confirm-popover',
        backdropDismiss: false,
      });
      await popover.present();
      const { data } = await popover.onDidDismiss();
      return data === true;
    }
  }

  async chooseSafety(item: SafetyDetails) {
    Logger.api(` chooseSafety: country=${cleanString(item.country)}, region=${cleanString(item.region)}, version=${item.version}, index=${item.index}, isAH=${this.isAH}, channelType=${this.channelType}`);

    const content = this.labels.upgradeSafetyConfirmMessage
      .replace('{country}', cleanString(item.country) ?? 'unknown')
      .replace('{region}', cleanString(item.region) ?? 'unknown')
      .replace('{version}', cleanString(item.version) ?? 'unknown');
    const title = this.labels.confirmTitle;

    const confirm = await this.openConfirmDialog(title, content);
    if (!confirm) {
      return;
    }

    Logger.api(` confirmed — channelHandler type=${this.channelHandler.handler.type}, command=${!!this.channelHandler.handler.channel.command}`);

    const state = this.updateState;
    state.selectedSafety = `${item.country} - ${item.region} - ${item.version}`;
    state.filteredSafety = [];
    state.selectedSafetyIndex = item.index;
    state.errorMessage = null;

    this.upgradeSGPService.state$.next(state);

    const config = {
      waitToCheckInSecs: 1,
      retryCountOnInitialCheck: 30,
    };

    this.logConfigurationSafeties(item);

    // AH: ensure the Modbus channel is open before writing safety blocks
    if (this.isAH && !this.channelHandler.handler.channel.command) {
      Logger.api(` AH: opening channel before safety write...`);
      const channel = CommunicationChannelType.InternetAH_ZCS;
      this.channelHandler.switch(channel);
      const internetInput: TypeInternetDevice = {
        serial: this._inverterId$.value || '',
        gatewaySN: this.gatewaySN,
        modbusAddress: this.modbusAddress,
        deviceType: 'inverter',
      };
      if (this.channelHandler.handler.type === channel) {
        await this.channelHandler.handler.channel.open(internetInput as any);
      }
      Logger.api(` AH: channel opened, command=${!!this.channelHandler.handler.channel.command}`);
    }

    Logger.api(` calling startUpdate(['SAFETY_CONFIG'])...`);
    await this.upgradeSGPService.startUpdate(['SAFETY_CONFIG'], config);
    Logger.api(` startUpdate completed, errorMessage=${this.updateState.errorMessage}`);
    // Re-read safety details after write to update "Versione corrente"
    this._safetyTrigger$.next();
  }

  async ngOnDestroy() {
    await this.channelHandler.handler.channel.close();
  }
}
